# Общи условия

**Дата на влизане в сила: 1 септември 2026 г.**

Настоящите Общи условия („Условията") уреждат достъпа ви до и използването на платформата {{companyName}}, достъпна на адрес [{{domain}}]({{url}}) („Услугата"), управлявана от {{legalName}}, българско дружество с променлив капитал (ДПК), ЕИК {{uic}}, ДДС № {{vatNumber}}, със седалище и адрес на управление: {{registeredAddress}} („ние", „нас" или „наш").

Като се регистрирате или използвате Услугата, вие потвърждавате, че сте прочели, разбрали и се съгласявате да бъдете обвързани от настоящите Условия. Ако не сте съгласни, не трябва да използвате Услугата.

---

## 1. За Услугата

{{companyName}} е уеб-базирана платформа за проследяване на имоти и сътрудничество по тях. В зависимост от наличната функционалност, Услугата предоставя: подаване на URL адреси на обяви с автоматично извличане на данни, записи за имоти с прикрепени всички обявления за даден имот, периодична повторна проверка на проследяваните обяви и откриване на промени (цена, наличност и други полета), сигнали в приложението и по имейл, етапи на имотите, календар за прегледи и наличност с истински календарни покани, изпращани по имейл, работни пространства за сътрудничество с членове, поканени по имейл или чрез линк за споделяне, пул от имоти на организацията, персонализирани полета, лента с активност, ръчно въвеждане на имоти като първокласен източник, и качване на изображения (галериите на обявите са публични; изображенията, качени от потребители, са частни за тяхната организация или работно пространство).

Данните за имотите се събират от публично достъпни сайтове за обяви — при стартиране, imot.bg — и чрез ръчно въвеждане. Допълнителни източници могат да се добавят с течение на времето.

**Планирани функции** (като допълнителни източници, push известия или платени планове) не са част от Услугата, докато не бъдат официално пуснати. Обявяването на планирани функции предварително не създава задължение за предоставянето им към определена дата или изобщо.

Услугата е предназначена за физически лица, проследяващи имоти за лични цели, и за агенции за недвижими имоти и техните екипи.

**Услугата не е агенция за недвижими имоти** и не предоставя брокерски, посреднически, ескроу услуги или услуги по обработка на плащания при покупка, продажба или наемане на имоти. Всички решения относно даден имот са изцяло отговорност на потребителя.

**Данните за имотите и обявите може да са неточни или остарели.** Те се събират от източници на трети страни и не можем да гарантираме тяхната точност, пълнота или актуалност. Винаги проверявайте информацията срещу оригиналната обява и срещу самия имот, преди да действате на нейна основа.

---

## 2. Изисквания за допустимост

За да се регистрирате и използвате Услугата, трябва да:

- Навършили сте поне 16 години
- Сте физическо лице, проследяващо имоти за лични цели, или действате от името на законно регистрирана агенция за недвижими имоти, оперираща на пазар, който Услугата обхваща
- Предоставите точна, пълна и вярна информация при регистрацията
- Имате правомощията да приемате настоящите Условия от свое или от името на вашата организация

**Защита на потребителите.** Ако използвате Услугата изключително за лични цели, извън която и да е професионална или търговска дейност, към вас се прилага законодателството за защита на потребителите. Когато договаряте в рамките на вашата професионална или търговска дейност (например като агенция за недвижими имоти), правилата за защита на потребителите не се прилагат за това използване.

Запазваме си правото да проверяваме легитимността на организациите от тип агенция и техните потребители и да отхвърляме или прекратяваме акаунти, които не отговарят на тези изисквания. Акаунти, изглеждащи като спам, непълни или несвързани с реално физическо лице или агенция, ще бъдат отхвърляни без предизвестие.

---

## 3. Акаунт, организации и работни пространства

### 3.1 Организации и работни пространства

Всеки потребител получава лична организация с едно работно пространство по подразбиране; нито едното от двете не може да бъде изтрито или архивирано. Организациите от тип агенция се управляват от **собственик**, който може да кани допълнителни членове с ролите **мениджър** или **стандартен**. Организацията отговаря за действията на всички свои членове.

Работните пространства са примитивът за сътрудничество: всеки член на работно пространство може да добавя и премахва имоти, задава етапи, редактира данните и персонализираните полета на пространството, създава календарни събития и кани други. Бележките, написани в споделено работно пространство, са видими за всичките му членове. Данните на ниво организация (вътрешни бележки, коригирани адреси, вътрешни цени) никога не са видими в работни пространства, дори в създадени от самата организация.

### 3.2 Сигурност на акаунта

Вие носите отговорност за:

- Пазенето в тайна на вашите данни за вход
- Всички дейности, извършвани чрез вашия акаунт
- Поддържането на актуални данни за контакт
- Незабавното ни уведомяване на **{{contactEmail}}** при съмнение за неоторизиран достъп до акаунта ви

Не трябва да споделяте акаунта си с друго лице.

### 3.3 Потребители в множество организации

Един потребител може да членува в няколко организации и работни пространства. Данните са строго разделени между организациите и всяка организация може да достъпва само своите собствени данни.

---

## 4. Такси и планове

Услугата в момента **се предоставя безплатно**.

Може да въведем платени планове и платени функции по-късно — например ценообразуване на база места за организациите от тип агенция или по-високи лимити за използване. Нашето текущо намерение е основното лично проследяване на имоти (проследяване, сигнали за промяна, личната организация и нейното работно пространство, етапи, прегледи) да остане достъпно безплатно. При въвеждане на платени планове или промяна на цените, това ще бъде обявено с поне 30 дни предварително, а приложимите цени ще бъдат публикувани, преди да поемете ангажимент за покупка.

**Всички цени са без ДДС**, когато е приложимо. ДДС се начислява допълнително по приложимата българска ставка (в момента 20%). Доставчикът е регистриран по ЗДДС с № {{vatNumber}}. Промените в цените не засягат вече предплатен период.

---

## 5. Допустимо използване и задължения на Клиента

### 5.1 Допустимо използване

Можете да използвате Услугата само за законни цели и в съответствие с настоящите Условия. Съгласявате се да:

- Използвате Услугата законосъобразно и само за проследяване на имоти и сътрудничество по тях
- Въвеждате само данни, които имате право да обработвате съгласно приложимото законодателство
- Ако сте организация от тип агенция: изпълнявате задълженията си като **администратор на лични данни** спрямо вашите членове и клиентите, които каните в работни пространства (предоставяне на информация, установяване на правно основание, отговаряне на права на субектите на данни) — вж. Раздел 8

### 5.2 Забранено поведение

Съгласявате се да не:

- Използвате Услугата за цели, незаконни или забранени от настоящите Условия
- Подавате невярна, подвеждаща или измамна информация
- Опитвате се да получите неоторизиран достъп до каквато и да е част от Услугата или нейната инфраструктура
- Пречите на или нарушавате целостта или производителността на Услугата
- Качвате файлове, съдържащи злонамерен софтуер, вируси или какъвто и да е вреден код
- Използвате Услугата за изпращане на нежелани съобщения или спам
- Извършвате обратно инженерство, декомпилиране или опит за извличане на изходния код на Услугата
- Възпроизвеждате, преразпространявате, препродавате или сублицензирате каквато и да е част от Услугата без нашето предварително писмено съгласие
- Опитвате се да извършвате автоматизирано извличане на данни или скрейпинг от Услугата
- Изтегляте масово или преразпространявате съдържание (като фотографии и описания от обяви), получено от източниците чрез Услугата

### 5.3 Спиране на акаунт

При съществено нарушение на настоящите Условия можем да ограничим или спрем достъпа след предоставяне на покана за отстраняване и разумен срок за отстраняване на нарушението. При особено тежки нарушения (злоупотреба, риск за сигурността, измама) можем да спрем достъпа незабавно без предизвестие.

---

## 6. Достъпност на Услугата

### 6.1 Достъпност и поддръжка

Целим се Услугата да бъде налична и работеща. Може да извършваме планирана поддръжка, която ще бъде обявявана поне 48 часа предварително, когато е възможно, и извършвана извън часовете на пик, доколкото е осъществимо. Не гарантираме, че Услугата ще бъде непрекъсната или без грешки.

### 6.2 Промени в Услугата

Запазваме си правото да променяме, развиваме или спираме отделни функционалности на Услугата по всяко време. Съществено ограничаване на платена функционалност в рамките на предплатен период дава право на пропорционално възстановяване или удължаване на периода.

---

## 7. Интелектуална собственост

### 7.1 Интелектуална собственост на Доставчика

Цялото съдържание, дизайн, код, търговски марки, лога и материали в Услугата са изключителна собственост на {{legalName}} или неговите лицензодатели и са защитени от приложимото законодателство за интелектуална собственост.

Получавате ограничен, неизключителен, непрехвърляем лиценз за достъп до и използване на Услугата единствено за предвидената й цел, описана в настоящите Условия, за срока на договора. Нищо в настоящите Условия не прехвърля каквито и да е права на собственост към вас.

### 7.2 Данни на Клиента

**Данните, въведени от потребителите и Организациите (включително ръчно въведените имоти, бележки, персонализираните полета, етапите и качените фотографии), остават собственост и отговорност на въвеждащия потребител или Организация.** Доставчикът не придобива права върху тях, освен ограниченото право да ги обработва за предоставяне на Услугата съгласно [Договора за обработване на данни]({{dpaUrl}}).

Файловете, които качвате в Услугата (както фотографии и планове), остават ваша собственост. Като ги качвате, вие ни предоставяте ограничен лиценз за съхраняването и обработването им единствено с цел предоставяне на Услугата на вас.

**Съдържанието на обявите произхожда от трети страни.** Фотографиите и текстът, прихванати от сайтовете за обяви, принадлежат на съответните им собственици (източниците и рекламодателите). Те се извличат и показват единствено с цел проследяване на имота в Услугата, и не можете да ги изтегляте или преразпространявате извън Услугата.

---

## 8. Защита на данните

### 8.1 Роли на администратор и обработващ лични данни

За личните данни, които контролират Организациите (техните членове, клиентите, които канят в работни пространства, и съдържанието за сътрудничество, което създават), **Организацията е администраторът на данните, а Доставчикът е обработващият лични данни**. Това отношение се урежда от [Договора за обработване на данни (ДОЛД)]({{dpaUrl}}), който е неразделна част от настоящите Условия.

### 8.2 Доставчикът като администратор на данни

За собствените данни на потребителските акаунти, данните за автентикация, техническите логове, аналитиката и комуникацията с нас, Доставчикът действа като администратор на данни. Това обработване е описано в нашата [Политика за поверителност]({{ppUrl}}).

### 8.3 Под-обработващи

Актуалният списък на под-обработващите е публикуван в приложението към [Договора за обработване на данни (ДОЛД)]({{dpaUrl}}).

---

## 9. Конфиденциалност

Всяка страна пази в тайна неоповестената търговска и техническа информация на другата страна, станала ѝ известна във връзка с договора, за срока на договора и 3 години след него.

---

## 10. Отговорности и ограничения

### 10.1 Отказ от отговорност за платформата

Услугата се предоставя „в настоящия й вид" и „при наличност". В максималната степен, позволена от закона, отказваме всякакви гаранции, изрични или подразбиращи се, включително, но не само гаранции за продаваемост, годност за определена цел и ненарушаване на права.

Не гарантираме, че:

- Услугата ще бъде непрекъсната, без грешки или без вируси
- Всякакви грешки в Услугата ще бъдат отстранени
- Резултатите, получени от използването на Услугата, ще бъдат точни или надеждни
- Даден източник ще остане поддържан, наличен или разрешен за събиране на данни

### 10.2 Отговорност за архивиране на данни

Услугата е проектирана да архивира, а не да изтрива потребителско съдържание. При поискване предоставяме експорт на данните на Клиента в общоупотребим машинночетим формат (вж. Раздел 13.4). Независимо от това, Клиентът носи отговорност за поддържането на собствени копия на информация, критична за него.

### 10.3 Точност на данните за имотите

Услугата е инструмент за проследяване и сътрудничество. Данните за обявите и имотите се събират от източници на трети страни и може да са неточни, непълни или остарели. Всички решения относно даден имот — включително решения за покупка, наемане или преговори — са изцяло отговорност на потребителя. Не сме страна по никоя сделка с имот и не предоставяме брокерски, ескроу услуги или услуги по обработка на плащания.

---

## 11. Ограничаване на отговорността

### 11.1 Стандарт на отговорност

Доставчикът отговаря само за вреди, причинени умишлено или при груба небрежност, в съответствие с българското право (Закон за задълженията и договорите, Член 94).

### 11.2 Таван на отговорността

Общата отговорност на Доставчика за всички претенции, произтичащи от или свързани с настоящите Условия през една договорна година, се ограничава до **сумата, платена от Клиента през предходните 12 месеца**. Когато не са платени суми през предходните 12 месеца (например докато Услугата е безплатна), общата отговорност се ограничава до минималния размер, допуснат от приложимото императивно право.

### 11.3 Изключени вреди

В максималната степен, позволена от приложимото законодателство, {{legalName}} не носи отговорност за никакви косвени, случайни, специални, последващи или наказателни вреди, включително, но не само:

- Загуба на печалби, приходи или бизнес възможности
- Загуба на данни (освен когато е причинена от умишлено действие или груба небрежност на Доставчика)
- Вреди от решения или действия, взети въз основа на данни за имоти или обяви в Услугата (включително решения за покупка, наемане или преговори)
- Вреди, причинени от действия или бездействия на Клиента

---

## 12. Непреодолима сила

Никоя страна не носи отговорност за неизпълнение, причинено от форсмажорни обстоятелства, включително, но не само: природни бедствия, война, тероризъм, стачки, действия на правителството, и откази на трети доставчици на инфраструктура (включително блокиране или влошаване на събирането на данни от източниците) извън разумния контрол на Доставчика (в съответствие с Член 306 от българския Търговски закон).

---

## 13. Срок и прекратяване

### 13.1 Продължителност на договора

Договорът се сключва за неопределен срок и действа, докато акаунтът на Клиента е активен. Изтриването на акаунта прекратява договора за Клиента и организациите, които той притежава.

### 13.2 Прекратяване от Клиента

Клиентът може да прекрати по всяко време, като изтрие акаунта си чрез секцията **„Изтрий акаунт"** в настройките. Суми, вече предплатени за бъдещ период, не се възстановяват, освен както е предвидено в Раздел 6.2.

### 13.3 Прекратяване от Доставчика

Запазваме си правото да спрем или окончателно да прекратим акаунта ви, със или без предизвестие, ако:

- Нарушите разпоредба на настоящите Условия
- Акаунтът ви се окаже, че не принадлежи на реално физическо лице или законно регистрирана агенция
- Акаунтът ви изглежда, че се използва за измамни, злоупотребяващи или незаконни цели
- Сме задължени да го направим от приложимото законодателство или компетентен орган

### 13.4 Последици от прекратяването

При прекратяване се прилагат правилата на [Договора за обработване на данни (ДОЛД)]({{dpaUrl}}) за връщане и изтриване на данните. Ще предоставим експорт на данни при поискване и ще изтрием или анонимизираме данни по указание на Организацията, в съответствие с ДОЛД. Изтриването на акаунт архивира личната организация и всички агенции, които потребителят притежава; съдържанието, което потребителят е авторствал в чужди работни пространства, остава на място, атрибутирано на изтрит потребител.

---

## 14. Промени в настоящите Условия

Можем периодично да актуализираме настоящите Условия с предизвестие от поне **30 дни** по имейл и/или чрез уведомление в приложението. При актуализация ще променим датата на влизане в сила в началото на документа.

Ако не приемате измененията, можете да прекратите акаунта си преди те да влязат в сила. Продължаването на използването на Услугата след влизането в сила на актуализираните Условия представлява ваше приемане на преработените Условия.

---

## 15. Съобщения

Официалните съобщения се изпращат на **{{contactEmail}}** (към нас) и на имейл адреса на собственика на акаунта (към Клиента). Съобщенията се считат за получени в първия работен ден след изпращането.

---

## 16. Приложимо право и спорове

Настоящите Условия се уреждат и тълкуват в съответствие със законодателството на **Република България** и приложимите регламенти на Европейския съюз.

Всякакви спорове, произтичащи от или свързани с настоящите Условия, подлежат на изключителната юрисдикция на компетентните съдилища в **София, България** (седалището на Доставчика).

---

## 17. Език

Условията са съставени на български и английски език. **При противоречие предимство има българският текст.**

---

## 18. Свържете се с нас

Ако имате въпроси относно настоящите Условия, моля свържете се с нас:

**{{legalName}}**  
{{registeredAddress}}  
Имейл: {{contactEmail}}
